import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import styles from './viewContract.module.css';
import { useBreadcrumbs } from '../../../components/ui/breadcrumps/breadcrumbsContext';
import { useCRUD } from '../../../api/crud';
import { CONTRACT_TERMS_ENDPOINT, CONTRACTS_ENDPOINT, SETTLEMENTS_ENDPOINT } from '../../../consts';
import { useHasPermission } from '../../../contexts/auth';
import { useListCount } from '../../../hooks/useListCount';
import { TabCountBadge } from '../../../components/ui/tabCountBadge/tabCountBadge.component';

export const ViewContract = () => {
  const { t } = useTranslation();
  const canViewSaleSummary = useHasPermission('settlement.view_sale_summary');
  const canViewHistory = useHasPermission('analytics.view_instancehistoryrecord');
  const canViewContractTerms = useHasPermission('settlement.view_contractterm');
  const canViewSettlements = useHasPermission('settlement.view_settlement');
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { setOverride } = useBreadcrumbs();
  const { get } = useCRUD(CONTRACTS_ENDPOINT);

  useEffect(() => {
    const fetchContract = async () => {
      if (id) {
        const response = await get(id);
        setOverride(
          `/contracts/${id}`,
          response.data ? response.data.number || response.data.name : ""
        );
      }
    };

    const handleContractUpdated = () => {
      fetchContract();
    };

    window.addEventListener('contractUpdated', handleContractUpdated);
    fetchContract();

    return () => {
      window.removeEventListener('contractUpdated', handleContractUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, get]);

  const pathname = location.pathname;
  const contractIdMatch = pathname.match(/\/contracts\/([^/]+)/);
  const contractId = contractIdMatch ? contractIdMatch[1] : null;
  const basePath = contractId ? `/contracts/${contractId}` : '/contracts';

  const equipmentCount = useListCount(
    contractId ? `${CONTRACT_TERMS_ENDPOINT}?contract=${contractId}` : null,
    canViewContractTerms
  );
  const settlementsCount = useListCount(
    contractId ? `${SETTLEMENTS_ENDPOINT}?contract_id=${contractId}` : null,
    canViewSettlements
  );
  const tabCounts: Record<string, number | undefined> = {
    equipment: equipmentCount,
    settlements: settlementsCount,
  };

  const visibleTabs = [
    { key: 'basic', path: basePath },
    { key: 'equipment', path: `${basePath}/equipment` },
    { key: 'settlements', path: `${basePath}/settlements` },
    ...(canViewHistory
      ? [{ key: 'history' as const, path: `${basePath}/history` }]
      : []),
    ...(canViewSaleSummary
      ? [{ key: 'saleSummary' as const, path: `${basePath}/sale-summary` }]
      : []),
  ];

  const getTabValue = () => {
    if (pathname.includes('/settlements')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'settlements');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/sale-summary')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'saleSummary');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/history')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'history');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/equipment')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'equipment');
      return index >= 0 ? index : 0;
    }
    return 0;
  };

  return (
    <Box className={styles.container}>
      <Tabs
        value={getTabValue()}
        variant="scrollable"
        scrollButtons="auto"
      >
        {visibleTabs.map((tab) => {
          const labels: Record<string, string> = {
            basic: t("Basic info"),
            equipment: t("Equipments"),
            settlements: t("Settlements"),
            history: t('Contract History'),
            saleSummary: t("Sale summary"),
          };
          return (
            <Tab
              key={tab.key}
              label={<TabCountBadge label={labels[tab.key]} count={tabCounts[tab.key]} />}
              onClick={() => navigate(tab.path)}
            />
          );
        })}
      </Tabs>
      <Box className={styles.content}>
        <Outlet />
      </Box>
    </Box>
  );
};
